import { Grid } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';

import { PermissionsEnum } from '@/auth/interfaces';
import {
  generateUUID,
  gridSizeMdLg4,
  gridSizeMdLg6,
  useCheckPermissions,
  useLoaders,
  useTableColumnsProducto,
  useTableFilter,
  useTableServerSideFiltering,
} from '@/shared';
import {
  useFetchBodegas,
  useFetchCategoryProductEnabled,
  useFetchUbicacionProductosEnabled,
  useFetchUbicaciones,
} from '@/shared/actions/erp';
import {
  CustomAutocomplete,
  GeneralTable,
  SampleDatePicker,
  SearchComponente,
  SingleButton,
} from '@/shared/components/ui';
import { CustomToggleSection } from '@/shared/components/ui/Sections';
import {
  AlertSeverityEnum,
  IVAEnumSelect,
  type Contrato,
} from '@/shared/interfaces';
import type {
  BODEGAS,
  CATEGORYPRODUCT,
  IUbicacionProducto,
  Ubicaciones,
} from '@/shared/interfaces/erp/inventario';
import { MaterialesKey, useMaterialesUtilizadosStore } from '@/store/erp';
import { useUiStore } from '@/store/ui';
import type { RubrosClienteFormDataType } from '../ClienteRuboLibreModal';

export type ClienteRubroLibrePopoverProps = {
  contract?: Contrato;

  form: UseFormReturn<RubrosClienteFormDataType>;
};

const ClienteRubroLibrePopover: React.FC<ClienteRubroLibrePopoverProps> = ({
  form,
}) => {
  useCheckPermissions(PermissionsEnum.api_view_ubicacionproducto);

  /////* local state ==============================
  const [addProductVisible, setAddProductVisible] = useState<boolean>(false);

  /////* global state ==============================
  const setAlert = useUiStore(s => s.setAlert);
  // rubro table -------------
  const addSelectedItem = useMaterialesUtilizadosStore(
    s => s.addSelectedItemCustomKey
  );

  /////* form ==============================
  const { errors } = form?.formState;
  const watchedBodega = form.watch('bodega');
  const watchedUbicacion = form.watch('ubicacion');
  const watchedCategory = form.watch('categoria_producto');

  /////* fetch data ==============================
  const {
    data: bodegasPaging,
    isLoading: isLoadingBodegas,
    isRefetching: isRefetchingBodegas,
  } = useFetchBodegas(
    {
      page_size: 900,
    },
    !!addProductVisible
  );
  const {
    data: ubicacionesPaging,
    isLoading: isLoadingUbicaciones,
    isRefetching: isRefetchingUbicaciones,
  } = useFetchUbicaciones(
    {
      page_size: 900,
      bodega: watchedBodega,
    },
    !!watchedBodega
  );
  const {
    data: categoryProductPaging,
    isLoading: isLoadingCategoryProduct,
    isRefetching: isRefetchingCategoryProduct,
  } = useFetchCategoryProductEnabled({
    enabled: true,
    params: {
      page_size: 900,
    },
  });

  /////* table ==============================
  // server side filters - colums table
  const { filterObject /* , columnFilters, setColumnFilters */ } =
    useTableServerSideFiltering();
  const {
    pagination,
    searchTerm,
    globalFilter,
    onChangeFilter,
    setPagination,
  } = useTableFilter();
  const { pageIndex, pageSize } = pagination;

  const {
    data: itemsDisponiblesPaging,
    isLoading: isLoadingItemsDisponibles,
    isRefetching: isRefetchingItemsDisponibles,
  } = useFetchUbicacionProductosEnabled({
    enabled: !!watchedCategory && !!watchedUbicacion,
    params: {
      page: pageIndex + 1,
      page_size: pageSize,
      ...filterObject,
      categoria_producto: watchedCategory,
      ubicacion: watchedUbicacion,
      producto: searchTerm,
    },
  });

  ///* columns ---------------------
  const { columnsCheckMovimientoInventario } = useTableColumnsProducto();

  /////* handlers ==============================
  const onAddEmptyLine = () => {
    const uuid = generateUUID();
    const newLine = {
      id: uuid,
      codigo: uuid,
      cantidad: 0,
      precio_1: 0,
      ivaCode: IVAEnumSelect.IVA_0_CODE,
      ivaValue: IVAEnumSelect.IVA_0_VALUE,
      total: 0,
      cantidadUtilizada: 1,
      series: [],
      contieneSeries: false,
      selectedSeries: [],
      savedSeries: [],
      isNewLine: true,
    };

    addSelectedItem(newLine, MaterialesKey.selectedItems, 'id', false, true);
  };

  /////* effects ==============================
  useEffect(() => {
    if (!watchedCategory || !watchedUbicacion) return;
    if (isLoadingItemsDisponibles || isRefetchingItemsDisponibles) return;

    !itemsDisponiblesPaging?.data?.count &&
      setAlert(
        true,
        'No cuenta con productos en la ubicacion seleccionada',
        AlertSeverityEnum.warning
      );
  }, [
    watchedCategory,
    watchedUbicacion,
    isLoadingItemsDisponibles,
    itemsDisponiblesPaging,
    isRefetchingItemsDisponibles,
  ]);

  useLoaders(
    isLoadingBodegas ||
    isRefetchingBodegas ||
    isLoadingUbicaciones ||
    isRefetchingUbicaciones ||
    isLoadingCategoryProduct ||
    isRefetchingCategoryProduct ||
    isLoadingItemsDisponibles ||
    isRefetchingItemsDisponibles
  );

  return (
    <>
      <Grid item container xs={12} spacing={3}>
        {/* ============ date picker ============ */}
        <Grid item container md={6}>
          <SampleDatePicker
            label="Fecha Vencimiento"
            name="fecha_vencimiento"
            control={form.control}
            defaultValue={null}
            error={errors.fecha_vencimiento}
            helperText={errors.fecha_vencimiento?.message}
            size={gridSizeMdLg6}
            minDate={dayjs().format()}
          />
        </Grid>

        {/* ============ buttons ============ */}
        <Grid item container md={6} justifyContent="flex-end">
          {/* -------- add line -------- */}
          <SingleButton
            newCustomButton
            justifyContent="flex-end"
            label="AGREGRAR LINEA"
            variant="text"
            onClick={onAddEmptyLine}
          />

          {/* ----- add product ----- */}
          <SingleButton
            newCustomButton
            justifyContent="flex-end"
            label="AGREGRAR PRODUCTO"
            variant="text"
            onClick={() => {
              setAddProductVisible(true);
            }}
          />
        </Grid>

        {/* ============ product ============ */}
        <Grid item container xs={12} mb={4} spacing={3}>
          {addProductVisible ? (
            <>
              <CustomToggleSection labelTitle="Productos" customShowInitState>
                <CustomAutocomplete<BODEGAS>
                  label="Bodega"
                  name="bodega"
                  defaultValue={form.getValues().bodega || ''}
                  // options
                  valueKey="nombre"
                  actualValueKey="codigo"
                  options={bodegasPaging?.data.data || []}
                  isLoadingData={isLoadingBodegas || isRefetchingBodegas}
                  disableClearable
                  // errors
                  control={form.control}
                  error={errors.bodega as any}
                  helperText={errors.bodega?.message}
                  size={gridSizeMdLg4}
                />
                <CustomAutocomplete<Ubicaciones>
                  label="Ubicacion"
                  name="ubicacion"
                  defaultValue={form.getValues().ubicacion || ''}
                  // options
                  valueKey="nombre"
                  actualValueKey="codigo"
                  options={ubicacionesPaging?.data.data || []}
                  isLoadingData={
                    isLoadingUbicaciones || isRefetchingUbicaciones
                  }
                  disableClearable
                  // errors
                  control={form.control}
                  error={errors.ubicacion as any}
                  helperText={errors.ubicacion?.message}
                  size={gridSizeMdLg4}
                />
                <CustomAutocomplete<CATEGORYPRODUCT>
                  label="Categoria Producto"
                  name="categoria_producto"
                  defaultValue={form.getValues().categoria_producto || ''}
                  // options
                  valueKey="nombre"
                  actualValueKey="codigo"
                  options={categoryProductPaging?.data.data || []}
                  isLoadingData={
                    isLoadingCategoryProduct || isRefetchingCategoryProduct
                  }
                  disableClearable
                  // errors
                  control={form.control}
                  error={errors.categoria_producto as any}
                  helperText={errors.categoria_producto?.message}
                  size={gridSizeMdLg4}
                />

                {/* ----------- table ----------- */}
                <Grid item container xs={12} spacing={3} my={2}>
                  {!!watchedUbicacion && !!watchedCategory ? (
                    <>
                      <Grid item xs={12} md={6}>
                        <SearchComponente
                          onChange={onChangeFilter}
                          value={globalFilter}
                          text="producto por código"
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <GeneralTable<IUbicacionProducto>
                          columns={columnsCheckMovimientoInventario}
                          data={itemsDisponiblesPaging?.data?.data || []}
                          isLoading={isLoadingItemsDisponibles}
                          isRefetching={isRefetchingItemsDisponibles}
                          // // filters - server side
                          // enableManualFiltering={true}
                          // columnFilters={columnFilters}
                          // onColumnFiltersChange={setColumnFilters}
                          // // search
                          enableGlobalFilter={false}
                          // // pagination
                          pagination={pagination}
                          onPaging={setPagination}
                          rowCount={itemsDisponiblesPaging?.data?.count}
                          // // actions ========
                          enableColumnActions={true}
                          canEdit={false}
                          canAdd={true}
                          onAdd={ubicProd => {
                            const firstPrice =
                              ubicProd?.producto_data?.precios?.[0]?.precio || '0.00';

                            const contieneSeries = ubicProd?.series?.length > 0;
                            const { id, ...ubiProdRestante } = ubicProd;

                            addSelectedItem(
                              {
                                id: ubicProd?.producto_data?.id_producto,
                                // producto_data: ubicProd?.producto_data,
                                ...ubiProdRestante,
                                precio_1: +firstPrice,
                                ivaCode:
                                  ubicProd?.producto_data?.iva ||
                                  IVAEnumSelect.IVA_0_CODE ||
                                  '',
                                ivaValue:
                                  +(ubicProd?.iva_data?.porcentaje || 0) ||
                                  IVAEnumSelect.IVA_0_VALUE,
                                cantidadUtilizada: 1,

                                contieneSeries,
                                selectedSeries: [],

                                bodega: watchedBodega,
                                ubicacion: watchedUbicacion,
                              },
                              MaterialesKey.selectedItems,
                              'id',
                              false,
                              true
                            );
                          }}
                        />
                      </Grid>
                    </>
                  ) : null}
                </Grid>
              </CustomToggleSection>
            </>
          ) : null}
        </Grid>
      </Grid>
    </>
  );
};

export default ClienteRubroLibrePopover;
